/**
 * I18nContext
 * React context for internationalization
 * Orchestrates all i18n services and provides translation functionality to components
 */

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import type {
  I18nContextValue,
  TranslationParams,
  NumberFormatOptions,
  DateFormatOptions,
} from '../types/i18n';
import {
  LanguageDetectionService,
  languageDetectionService,
} from '../services/i18n/LanguageDetectionService';
import { StorageService, storageService } from '../services/i18n/StorageService';
import { TranslationLoader, translationLoader } from '../services/i18n/TranslationLoader';
import { FormatService, formatService } from '../services/i18n/FormatService';
import { DOMService, domService } from '../services/i18n/DOMService';
import {
  DEFAULT_LANGUAGE,
  STORAGE_KEY,
  ENABLE_AUTO_LANGUAGE_DETECTION,
} from '../config/i18n.config';

// Create context
const I18nContext = createContext<I18nContextValue | null>(null);

// Provider props
interface I18nProviderProps {
  children: React.ReactNode;
  // Allow service injection for testing
  languageDetectionService?: LanguageDetectionService;
  storageService?: StorageService;
  translationLoader?: TranslationLoader;
  formatService?: FormatService;
  domService?: DOMService;
}

/**
 * I18nProvider
 * Provides i18n context to all child components
 */
export const I18nProvider: React.FC<I18nProviderProps> = ({
  children,
  languageDetectionService: langDetectionService = languageDetectionService,
  storageService: storage = storageService,
  translationLoader: transLoader = translationLoader,
  formatService: formatter = formatService,
  domService: dom = domService,
}) => {
  // State
  const [currentLanguage, setCurrentLanguage] = useState<string>(DEFAULT_LANGUAGE);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [isAutoDetected, setIsAutoDetected] = useState<boolean>(false);

  /**
   * Change current language
   */
  const changeLanguage = useCallback(
    async (code: string): Promise<void> => {
      try {
        // Don't reload if same language
        if (code === currentLanguage) {
          return;
        }

        setIsLoading(true);
        setError(null);

        // Load translations for new language
        await transLoader.loadTranslation(code);

        // Update state
        setCurrentLanguage(code);
        setIsAutoDetected(false); // User manually changed language

        // Persist to storage (this marks it as user preference)
        storage.saveLanguage(code);

        // Clear auto-detected flag since user chose manually
        storage.clearAutoDetectedLanguage();

        // Update HTML attributes for accessibility
        dom.updateHTMLAttributes(code);
      } catch (err) {
        console.error('[I18nProvider] ❌ Error changing language:', err);
        setError(err instanceof Error ? err : new Error('Failed to change language'));
        throw err; // Re-throw for caller to handle
      } finally {
        setIsLoading(false);
      }
    },
    [currentLanguage, transLoader, storage, dom]
  );

  // Initialize language on mount
  useEffect(() => {
    const initializeLanguage = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Check and migrate storage if needed
        storage.checkAndMigrateStorage();

        let initialLanguage: string | null = null;
        let wasAutoDetected = false;

        if (ENABLE_AUTO_LANGUAGE_DETECTION) {
          // When language switching is enabled:
          // Priority: 1) Stored language (user preference)
          initialLanguage = storage.getLanguage();

          if (!initialLanguage) {
            // 2) Auto-detect from browser
            const detected = langDetectionService.detectBrowserLanguage();
            if (detected && detected !== DEFAULT_LANGUAGE) {
              initialLanguage = detected;
              wasAutoDetected = true;
              // Save auto-detected language for showing "Auto-detected" message
              storage.saveAutoDetectedLanguage(detected);
            }
          }
        } else {
          // Language switching disabled - clear any stored preference to reset to English
          // This prevents users from being stuck on bad NLLB translations with no UI to switch back
          storage.clearAutoDetectedLanguage();
        }

        if (!initialLanguage) {
          // Final fallback: default to English
          initialLanguage = DEFAULT_LANGUAGE;
        }

        // Load translations for initial language
        await transLoader.loadTranslation(initialLanguage);

        // Update state
        setCurrentLanguage(initialLanguage);
        setIsAutoDetected(wasAutoDetected);

        // Update HTML attributes for accessibility
        dom.updateHTMLAttributes(initialLanguage);

        // Preload top languages in background (only when language switching is enabled)
        if (ENABLE_AUTO_LANGUAGE_DETECTION) {
          transLoader.preloadTopLanguages();
        }
      } catch (err) {
        console.error('[I18nProvider] Error initializing language:', err);
        setError(err instanceof Error ? err : new Error('Failed to initialize language'));
        // Still set to default language on error
        setCurrentLanguage(DEFAULT_LANGUAGE);
      } finally {
        setIsLoading(false);
      }
    };

    initializeLanguage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount - services are stable

  // Listen for storage changes (language change in another tab)
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY && event.newValue) {
        const newLanguage = event.newValue;
        if (newLanguage !== currentLanguage) {
          changeLanguage(newLanguage);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [currentLanguage, changeLanguage]);

  /**
   * Translation function
   */
  const t = useCallback(
    (
      key: string,
      defaultValueOrParams?: string | TranslationParams,
      params?: TranslationParams
    ): string => {
      try {
        let finalParams: TranslationParams | undefined;
        if (typeof defaultValueOrParams === 'string') {
          // t(key, defaultValue, params?) overload
          finalParams = { ...params, defaultValue: defaultValueOrParams };
        } else {
          // t(key, params?) overload
          finalParams = defaultValueOrParams;
        }
        return transLoader.getTranslation(key, finalParams);
      } catch (err) {
        console.error('[I18nProvider] Error translating key:', key, err);
        return key; // Return key as fallback
      }
    },
    [transLoader]
  );

  /**
   * Get text direction for current language
   */
  const direction = useMemo(() => {
    return langDetectionService.getLanguageDirection(currentLanguage);
  }, [currentLanguage, langDetectionService]);

  /**
   * Format number with current locale
   */
  const formatNumber = useCallback(
    (value: number, options?: NumberFormatOptions) => {
      return formatter.formatNumber(value, currentLanguage, options);
    },
    [currentLanguage, formatter]
  );

  /**
   * Format date with current locale
   */
  const formatDate = useCallback(
    (date: Date, options?: DateFormatOptions) => {
      return formatter.formatDate(date, currentLanguage, options);
    },
    [currentLanguage, formatter]
  );

  /**
   * Format currency with current locale
   */
  const formatCurrency = useCallback(
    (amount: number, currency: string) => {
      return formatter.formatCurrency(amount, currency, currentLanguage);
    },
    [currentLanguage, formatter]
  );

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<I18nContextValue>(() => {
    return {
      currentLanguage,
      isLoading,
      error,
      t,
      changeLanguage,
      direction,
      formatNumber,
      formatDate,
      formatCurrency,
      isAutoDetected,
    };
  }, [
    currentLanguage,
    isLoading,
    error,
    t,
    changeLanguage,
    direction,
    formatNumber,
    formatDate,
    formatCurrency,
    isAutoDetected,
  ]);

  // Show loading screen while initializing translations
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600">Loading translations...</p>
        </div>
      </div>
    );
  }

  return <I18nContext.Provider value={contextValue}>{children}</I18nContext.Provider>;
};

/**
 * Hook to use i18n context
 * Throws error if used outside provider
 */
// eslint-disable-next-line react-refresh/only-export-components
export const useI18n = (): I18nContextValue => {
  const context = useContext(I18nContext);

  if (!context) {
    throw new Error('useI18n must be used within I18nProvider');
  }

  return context;
};
